import { zodResolver } from "@hookform/resolvers/zod";
import { KdsModal, VStack } from "@kazaar-platform/design-system";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslate } from "@tolgee/react";
import { useAtomValue } from "jotai";
import { FormProvider, useForm } from "react-hook-form";
import { useUpdateUser } from "@/api/__generated__/orval/auth";
import { ControlledTextField } from "@/components/Form";
import { userAtom } from "@/store";
import {
  type SetupUserFormType,
  setupUserFormSchema,
} from "./ModalSetupUserInfo.schema";

export const ModalSetupUserInfo = () => {
  const { t } = useTranslate();
  const user = useAtomValue(userAtom);

  const queryClient = useQueryClient();
  const { mutate: updateUser, isPending } = useUpdateUser({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      },
    },
  });

  const methods = useForm<SetupUserFormType>({
    defaultValues: {
      firstName: "",
      lastName: "",
    },
    resolver: zodResolver(setupUserFormSchema),
  });

  const onSubmit = (values: SetupUserFormType) => {
    if (!user?.id) {
      return;
    }

    updateUser({
      id: user?.id,
      data: {
        firstName: values.firstName,
        lastName: values.lastName,
      },
    });
  };
  return (
    <KdsModal
      isOpen={true}
      isDismissable={false}
      size="sm"
      dialogProps={{
        dialogHeaderProps: {
          title: t("layout.setupUserModal.title"),
        },
        content: (
          <FormProvider {...methods}>
            <VStack gap="sp-md">
              <ControlledTextField
                name="firstName"
                label={t("layout.setupUserModal.firstName")}
              />
              <ControlledTextField
                name="lastName"
                label={t("layout.setupUserModal.lastName")}
              />
            </VStack>
          </FormProvider>
        ),
        dialogFooterProps: {
          buttonProps: [
            {
              children: t("buttons.save"),
              onPress: () => methods.handleSubmit(onSubmit)(),
              isLoading: isPending,
            },
          ],
        },
      }}
    />
  );
};
